import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { mockDocuments, mockFollowUps } from '../../data/mockData';

const badge = (label) => (
  <Badge variant="outline" className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
    {label}
  </Badge>
);

export default function EmployeeAlerts() {
  const location = useLocation();
  const isMissingDocs = location.pathname.includes('/employee/alerts/missing-documents');

  const overdueFollowUps = useMemo(() => mockFollowUps.filter((f) => f.status === 'Overdue'), []);
  const missingDocs = useMemo(() => mockDocuments.filter((d) => d.status === 'Pending'), []);

  return (
    <div className="space-y-6" data-testid="employee-alerts">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Alerts</h1>
          <p className="text-muted-foreground mt-1">Overdue tasks and missing documents</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant={isMissingDocs ? 'outline' : 'default'} size="sm">
            <Link to="/employee/alerts/overdue">Overdue Files</Link>
          </Button>
          <Button asChild variant={isMissingDocs ? 'default' : 'outline'} size="sm">
            <Link to="/employee/alerts/missing-documents">Missing Documents</Link>
          </Button>
        </div>
      </div>

      {!isMissingDocs ? (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Overdue Follow-Ups</CardTitle>
            <CardDescription>Follow-ups that need immediate action</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overdueFollowUps.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span>{f.leadName}</span>
                        <span className="text-xs text-muted-foreground font-mono">{f.leadId}</span>
                      </div>
                    </TableCell>
                    <TableCell>{f.type}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{f.scheduledDate}</span>
                        <span className="text-xs text-muted-foreground">{f.scheduledTime}</span>
                      </div>
                    </TableCell>
                    <TableCell>{badge(f.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{f.notes}</TableCell>
                  </TableRow>
                ))}
                {overdueFollowUps.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                      No overdue follow-ups.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Missing Documents</CardTitle>
            <CardDescription>Pending documents required to move files forward</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {missingDocs.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span>{d.leadName}</span>
                        <span className="text-xs text-muted-foreground font-mono">{d.leadId}</span>
                      </div>
                    </TableCell>
                    <TableCell>{d.docType}</TableCell>
                    <TableCell>{d.uploadDate}</TableCell>
                    <TableCell>{badge(d.status)}</TableCell>
                  </TableRow>
                ))}
                {missingDocs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10 text-muted-foreground">
                      No missing documents.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

